import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateShippingMethodPayload,
  type IShippingMethodRepository,
  type UpdateShippingMethodPayload,
} from '../../../business-logic/interfaces/IShippingMethodRepository'
import { type ShippingMethodConstructor } from '../../../entities/ShippingMethod'
import { type ShippingMethodApiModel } from '../../api-models/ShippingMethodApiModel'
import { mapShippingMethodApiModel } from '../../mappers/mapShippingMethodApiModel'
import { type GetShippingMethodsResponse } from '../../types/GetShippingMethodsResponse'

const SHIPPING_METHOD_URL = '/api/v3/methods/shipping/'

interface Dependency {
  httpClient: IHttpClient
}

export class ShippingMethodRepository implements IShippingMethodRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<ShippingMethodConstructor>> {
    const res = await this.deps.httpClient.get<GetShippingMethodsResponse>(
      SHIPPING_METHOD_URL,
      {
        params: formatRepoFiltersToApiFilters(params),
      }
    )

    const shippingMethods = res.data.data || []
    const metadata = res.data.metadata

    return {
      data: shippingMethods.map((shippingMethod) =>
        mapShippingMethodApiModel(shippingMethod)
      ),
      pagination: {
        count: metadata?.pagination?.count || 0,
        page: metadata?.pagination?.current_page || 0,
        pageSize: metadata?.pagination?.page_size || 0,
        numPages: metadata?.pagination?.num_pages || 0,
      },
    }
  }

  // Omits Angular's stray `id: null` / `text: ""` create fields — the server
  // ignores them
  async create(payload: CreateShippingMethodPayload): Promise<{ id: number }> {
    try {
      const res = await this.deps.httpClient.post<{
        data: ShippingMethodApiModel
      }>(SHIPPING_METHOD_URL, payload)

      return { id: res.data.data.id }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(
    id: number,
    payload: UpdateShippingMethodPayload
  ): Promise<void> {
    try {
      // PUT the whole row (Angular parity)
      await this.deps.httpClient.put(`${SHIPPING_METHOD_URL}${id}/`, {
        id,
        ...payload,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  // Canonical trailing slash — avoids the 301 hop Angular incurs
  async delete(id: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`${SHIPPING_METHOD_URL}${id}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
